/* eslint-disable no-mixed-operators */
import React, {
  useContext,
  createContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Button as RaButton,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import {
  dataGridErrorActions,
  dataGridHeaderCellFilter,
  margin1,
  padding1,
  padding2,
  removeNumberArrowsStyle,
  topActionButtonBaseStyle,
  dataGrid,
  cursor,
  iconAction,
  dataGridSummaryTitle
} from "../Styles";
import { ActionTitles } from "../Constants";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import CustomDialog from "../../utils/CustomDialog";
import { CircularProgress, Grid } from "@mui/material";
import "react-data-grid/lib/styles.css";
import DataGrid, { SelectColumn, textEditor } from 'react-data-grid';
import moment from "moment-timezone";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import {
  formatNumberToString,
  initKeyAndCalculateValidateAmounts,
  isDataUnchanged,
  mapReductionTransactions,
  reverseArray,
} from "../../utils/TransactionsUtils";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {deepCopy} from "../../utils/util";
import { concat } from "lodash";
import { isTransactionEditor } from "../DataProvider";
import { v4 as uuidv4 } from 'uuid';
import DatePicker from "react-datepicker";
import { createPortal } from "react-dom";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import { checkTokenExpiryAndRefresh } from "../../utils/LoginUtils";
import momentDate from "../../utils/moment-date";

const FilterContext = createContext(undefined);

/**
 *
 * @param tabIndex
 * @param column
 * @param children
 * @returns {Element}
 * @constructor
 */
const FilterRenderer = ({ tabIndex, column, children }) => {
  const filters = useContext(FilterContext);
  return (
    <>
      <div>{column.name}</div>
      {filters && filters.enabled && <div>{children({ tabIndex, filters })}</div>}
    </>
  );
}

/**
 * Transactions React Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export const Transactions = (props) => {
  const dataProvider = useDataProvider()
  const notify = useNotify();
  const gridRef = useRef(null);
  const [originalFullData, setOriginalFullData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [rows, setRows] = useState(() => []);
  const [deletedRows, setDeletedRows] = useState(() => []);
  const [insertedRows, setInsertedRows] = useState(() => []);
  const [errors, setErrors] = useState([]);
  const [scrollErrorRowIdx, setScrollErrorRowIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [updatingSaving, setUpdatingSaving] = useState(null);
  const [filters, setFilters] = useState(() => (
    {
      txnCode: '',
      description: '',
      bookingDateTime: '',
      amountMin: '',
      amountMax: '',
      balanceAmountMin: '',
      balanceAmountMax: '',
      enabled: true,
    }
  ));
  const [selectedTxnIds, setSelectedTxnIds] = useState(() => new Set());
  const [selectedTxnRows, setSelectedTxnRows] = useState(() => []);
  const enableEditing = isTransactionEditor();
  const lsSortDir = localStorage.getItem("txnSortASCDir")
  const [sortASCDirection, setSortASCDirection] = useState(lsSortDir ? lsSortDir === 'true' : true);
  const [pageNumber, setPageNumber] = useState(null);
  const [rowNumber, setRowNumber] = useState(null);

  function UnLoadWindow() {
    return !isDataUnchanged(rows, originalData) ? true : null
  }
  window.onbeforeunload = UnLoadWindow;

  const fetchTransactions = async () => {
    let data = [];
    dataProvider
      .getAllTransactions({ clientId: props.clientId, matterId: props.matterId, accountId: props.accountId })
      .then(({ content }) => {
        if (content && content.length)
          data = initKeyAndCalculateValidateAmounts(content, sortASCDirection, true)

        setRows(data);
        setOriginalData(mapReductionTransactions(deepCopy(data)))
        setOriginalFullData(deepCopy(data))
      })
      .catch((error) => {
        notify('Something went wrong while fetching the transactions', { type: 'warning', autoHideDuration: 1000 });
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * For each selected row Id selected, we select the whole row of properties
   */
  useEffect(() => {
    const selectedIds = [...selectedTxnIds.values()]
    const selectedTxnRowsTmp = []

    selectedIds.forEach(selectedId => selectedTxnRowsTmp.push(rows.find(item => item.transactionId === selectedId)))
    selectedTxnRowsTmp.sort((a, b) => a.key - b.key)

    setSelectedTxnRows(selectedTxnRowsTmp)
  }, [selectedTxnIds, rows]);

  /**
   * Check wether there are still errors within the list of transactions
   */
  useEffect(() => {
    const errorsTmp = []
    rows.forEach(row => {
      if (row.balanceAmountVsCalculatedBalanceError || row.insertedRow)
        errorsTmp.push(row)
    })

    setErrors(errorsTmp)
    if (errorsTmp.length === 0)
      setScrollErrorRowIdx(-1)
  }, [rows])

  /**
   *
   * @type {*[]}
   */
  const filteredRows = useMemo(() => {
    const copyRows = sortASCDirection ? reverseArray(rows) : rows
    return copyRows.filter((r) => {
      return (
        (filters.txnCode ? r.txnCode ? r.txnCode.toLowerCase().includes(filters.txnCode.toLowerCase()) : !!r.insertedRow : true) &&
        (filters.description ? r.description ? r.description.toLowerCase().includes(filters.description.toLowerCase()) : !!r.insertedRow : true) &&
        (filters.bookingDateTime ? moment(r.bookingDateTime.toLowerCase()).format("DD/MM/YYYY").includes(filters.bookingDateTime.toLowerCase()) : true) &&
        (filters.amountMin !== '' ? r.amount >= parseFloat(filters.amountMin) : true) &&
        (filters.amountMax !== '' ? r.amount <= parseFloat(filters.amountMax) : true) &&
        (filters.balanceAmountMin !== '' ? r.balanceAmount >= parseFloat(filters.balanceAmountMin) : true) &&
        (filters.balanceAmountMax !== '' ? r.balanceAmount <= parseFloat(filters.balanceAmountMax) : true)
      );
    });
  }, [rows, sortASCDirection, filters]);

  /**
   * Changes the direction of the sorting of the transactions table
   */
  const handleChangeSorting = () => {
    setUpdatingSaving("Updating")
    const copySortASCDirection = deepCopy(sortASCDirection)
    localStorage.setItem('txnSortASCDir', !sortASCDirection)
    setSortASCDirection(!sortASCDirection)
    const newRows = initKeyAndCalculateValidateAmounts(rows, !copySortASCDirection)
    setRows(newRows)
    setUpdatingSaving(null)
  }


  function getBalanceMovement(filteredRows, balanceType) {
    if (filteredRows.length <= 2) return null;
    if (filteredRows[0][balanceType] === null || filteredRows[filteredRows.length - 1][balanceType] === null) return null;
    let firstBalance = sortASCDirection ? parseFloat(filteredRows[0][balanceType]) : parseFloat(filteredRows[filteredRows.length - 1][balanceType]);
    let lastBalance = sortASCDirection ? parseFloat(filteredRows[filteredRows.length - 1][balanceType]) : parseFloat(filteredRows[0][balanceType]);
    let firstAmount = sortASCDirection ? parseFloat(filteredRows[0].amount) : parseFloat(filteredRows[filteredRows.length - 1].amount);
    return lastBalance - (firstBalance - firstAmount);
  }

  const summaryRows = [
    {
      totalRows: filteredRows.length,
      amount: filteredRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0).toFixed(2),
      balanceAmountMovement: getBalanceMovement(filteredRows, "balanceAmount"),
      calBalanceAmountMovement: getBalanceMovement(filteredRows, "calculatedBalance"),
      maxDate:
        filteredRows.length >= 2 ? (sortASCDirection ? momentDate(filteredRows[filteredRows.length - 1].bookingDateTime).format("DD/MM/YYYY") : momentDate(filteredRows[0].bookingDateTime).format("DD/MM/YYYY")) : null,
      minDate:
        filteredRows.length >= 2 ? (sortASCDirection ? momentDate(filteredRows[0].bookingDateTime).format("DD/MM/YYYY") : momentDate(filteredRows[filteredRows.length - 1].bookingDateTime).format("DD/MM/YYYY")) : null,
    }
  ];

  /**
   *
   * @type {[{readonly renderEditCell?: Maybe<(props: RenderEditCellProps<any, any>) => React.ReactNode>, readonly headerCellClass?: Maybe<string>, readonly summaryCellClass?: Maybe<string | ((row: any) => Maybe<string>)>, readonly renderSummaryCell?: Maybe<(props: RenderSummaryCellProps<any, any>) => React.ReactNode>, readonly resizable?: Maybe<boolean>, readonly editable?: Maybe<boolean | ((row: any) => boolean)>, readonly colSpan?: Maybe<(args: ColSpanArgs<any, any>) => Maybe<number>>, readonly sortDescendingFirst?: Maybe<boolean>, readonly frozen?: Maybe<boolean>, readonly minWidth?: Maybe<number>, readonly cellClass?: Maybe<string | ((row: any) => Maybe<string>)>, readonly editorOptions?: Maybe<{readonly displayCellContent?: Maybe<boolean>, readonly commitOnOutsideClick?: Maybe<boolean>}>, readonly sortable?: Maybe<boolean>, readonly renderGroupCell?: Maybe<(props: RenderGroupCellProps<any, any>) => React.ReactNode>, readonly renderHeaderCell?: Maybe<(props: RenderHeaderCellProps<any, any>) => React.ReactNode>, readonly draggable?: Maybe<boolean>, readonly name: string | React.ReactElement, readonly width?: Maybe<number | string>, readonly renderCell?: Maybe<(props: RenderCellProps<any, any>) => React.ReactNode>, readonly key: string, readonly maxWidth?: Maybe<number>},{renderEditCell: <TRow, TSummaryRow>({row, column, onRowChange, onClose}: RenderEditCellProps<TRow, TSummaryRow>) => JSX.Element, renderHeaderCell: (function(*): *), name: string, width: number, key: string},{renderEditCell: <TRow, TSummaryRow>({row, column, onRowChange, onClose}: RenderEditCellProps<TRow, TSummaryRow>) => JSX.Element, renderHeaderCell: (function(*): *), name: string, width: number, key: string},{renderEditCell: <TRow, TSummaryRow>({row, column, onRowChange, onClose}: RenderEditCellProps<TRow, TSummaryRow>) => JSX.Element, renderHeaderCell: (function(*): *), name: string, key: string},{renderEditCell: <TRow, TSummaryRow>({row, column, onRowChange, onClose}: RenderEditCellProps<TRow, TSummaryRow>) => JSX.Element, renderHeaderCell: (function(*): *), name: string, width: number, key: string},null,null,null]}
   */
  const columns = [
    ...(enableEditing ? [{ ...SelectColumn }] : []),
    {
      key: 'reposition',
      name: '',
      width: 78,
      renderCell(props) {
        return (
          <Grid container flexDirection={"row"} justifyContent={"space-between"} flexWrap={"nowrap"}>
            <Grid item>
              <RaButton
                id={`matters-transactions-reposition-down-${props.row.transactionId}`}
                size="small"
                sx={{...iconAction, minWidth: "auto"}}
                disabled={props.rowIdx === (rows.length - 1)}
                onClick={() => {
                  const filteredRowsCopy = deepCopy(filteredRows)
                  filteredRowsCopy[props.rowIdx].key = sortASCDirection ? filteredRowsCopy[props.rowIdx].key  - 1 : filteredRowsCopy[props.rowIdx].key + 1
                  handleRowChange(filteredRowsCopy, {indexes: [props.rowIdx], column: { key: 'key' }})
                }}
              >
                <ArrowDownwardIcon />
              </RaButton>
            </Grid>
            <Grid item>
              <RaButton
                id={`matters-transactions-reposition-up-${props.row.transactionId}`}
                size="small"
                sx={{...iconAction, minWidth: "auto"}}
                disabled={props.rowIdx === 0}
                onClick={() => {
                  const filteredRowsCopy = deepCopy(filteredRows)
                  filteredRowsCopy[props.rowIdx].key = sortASCDirection ? filteredRowsCopy[props.rowIdx].key + 1 : filteredRowsCopy[props.rowIdx].key - 1
                  handleRowChange(filteredRowsCopy, {indexes: [props.rowIdx], column: { key: 'key' }})
                }}
              >
                <ArrowUpwardIcon />
              </RaButton>
            </Grid>
          </Grid>
        );
      },
    },
    {
      key: 'key',
      name: '',
      width: 60,
      editable: enableEditing,
      renderEditCell: textEditor,
      renderHeaderCell: (p) => (
        <Grid container sx={{paddingBottom: "58px"}}>
          <Grid id="matters-transactions-sort-key" container alignItems={"center"} sx={{...cursor.pointer}} onClick={handleChangeSorting}>
            <Grid item sx={{fontSize: "18px"}}><span>#</span></Grid>
            <Grid item sx={{height: "21px"}}>
              { !sortASCDirection && <ArrowDownwardIcon sx={{height: "19px"}} /> }
              { sortASCDirection && <ArrowUpwardIcon sx={{height: "19px"}} /> }
            </Grid>
          </Grid>
        </Grid>
      ),
      renderSummaryCell({ row }) {
        return (
          <>
            <Grid container justifyContent="center" sx={{ fontSize: "14px" }}>
              <strong>{row.totalRows}</strong>
            </Grid>
            <Grid container justifyContent="center" sx={dataGridSummaryTitle}>
              <strong>{row.totalRows === 1 ? 'TXN' : 'TXNS'}</strong>
            </Grid>
          </>
        );
      }
    },
    {
      key: 'description',
      name: 'DESCRIPTION',
      editable: enableEditing,
      cellClass() {
        return "rdg-cell-description";
      },
      renderCell(props) {
        const value = props.row.description;
        return (
          <Grid item onMouseOver={() => { setPageNumber(props.row.pageNumber); setRowNumber(props.row.rowNumber)}} onMouseLeave={() => { setPageNumber(null); setRowNumber(null)}}>
            {value}
          </Grid>
        );
      },
      renderEditCell: textEditor,
      renderHeaderCell: (p) => (
        <FilterRenderer {...p}>
          {({ filters, ...rest }) => {
            return (
              <Grid container sx={{paddingBottom: "34px"}}>
                <Grid item flexGrow={1}>
                  <input
                    style={{...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.description}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        description: e.target.value
                      })
                    }
                  />
                </Grid>
              </Grid>
            )
          }}
        </FilterRenderer>
      )
    },
    {
      key: 'bookingDateTime',
      width: 120,
      editable: enableEditing,
      renderCell(props) {
        const value = props.row.bookingDateTime;
        return (
          <Grid item>
            {moment(value).format("DD/MM/YYYY")}
          </Grid>
        );
      },
      editorOptions: {
        displayCellContent: true
      },
      renderEditCell({ row, onRowChange, onClose }) {
        const originalBookingDate = new Date(row.bookingDateTime)
        const today = new Date()
        const index = filteredRows.findIndex((item) => item.transactionId === row.transactionId)

        let dateTime = row.bookingDateTime
        if (!row.bookingDateTime || (isNaN(new Date(row.bookingDateTime)))) {
          let found = null
          for (let i = index - 1; i >= 0; i--) {
            if (!isNaN(new Date(filteredRows[i].bookingDateTime))) {
              found = new Date(filteredRows[i].bookingDateTime)
              break
            }
          }

          if (!found) {
            for (let i = index + 1; i < filteredRows.length; i++) {
              if (!isNaN(new Date(filteredRows[i].bookingDateTime))) {
                found = new Date(filteredRows[i].bookingDateTime)
                break
              }
            }
          }

          dateTime = found || today.toISOString()
        }

        let bookingDate = new Date(dateTime)
        return createPortal(
            <Dialog open>
              <DatePicker
                inline
                wrapperClassName="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall"
                onFocus={(ev) => {
                  ev.preventDefault()
                  ev.target.setAttribute('inputmode', 'none')
                }}
                popperModifiers={{
                  flip: {
                    behavior: ["bottom"], // don't allow it to flip to be above
                  },
                  preventOverflow: {
                    enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
                  },
                  hide: {
                    enabled: false, // turn off since needs preventOverflow to be enabled
                  },
                }}
                selected={bookingDate}
                onChange={(e) => {
                  if (moment(e).format('DD/MM/yyyy') === moment(originalBookingDate).format('DD/MM/yyyy')) {
                    onClose(true)
                    return
                  }

                  row.bookingDateTime = moment.utc(e).format()
                  onRowChange(handleRowChange(filteredRows, {indexes: [filteredRows.findIndex(item => item.transactionId === row.transactionId)], column: { key: 'bookingDateTime' }}))
                  onClose(true)
                }}
                peekNextMonth
                maxDate={new Date()}
                showMonthDropdown
                showYearDropdown
                dateFormat="dd/MM/yyyy"
                dropdownMode="select"
              />
              <Grid container flexGrow={1} justifyContent={"center"} sx={padding2.y}>
                <Grid item>
                  <RaButton
                    id={`matters-transactions-date-cancel-${row.transactionId}`}
                    sx={{ ...topActionButtonBaseStyle("gray", "#fff", "#eaeaea", "#b2afaf", "#9B9999") }}
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    label={ActionTitles.CANCEL}
                    onClick={() => onClose()}
                  />
                </Grid>
              </Grid>
            </Dialog>,
          document.body
        );
      },
      renderHeaderCell: (p) => (
        <Grid>
          <Grid id="matters-transactions-sort-date" container alignItems={"center"} sx={{...cursor.pointer}} onClick={handleChangeSorting}>
            <Grid item>
              <span>DATE</span>
            </Grid>
          </Grid>
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => {
              return (
                <Grid container sx={{paddingBottom: "34px"}}>
                  <Grid item>
                    <input
                      type="text"
                      placeholder="dd/mm/yyyy"
                      maxLength="10"
                      style={{ ...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full }}
                      {...rest}
                      value={filters.bookingDateTime}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          bookingDateTime: e.target.value
                        })
                      }
                    />
                  </Grid>
                </Grid>
              )
            }}
          </FilterRenderer>
        </Grid>
      ),
      renderSummaryCell({ row }) {
        if (!row.minDate || !row.maxDate) {
          return null;
        }

        return (
          <>
            <Grid container justifyContent="center" sx={{ fontSize: "12px", position: 'relative' }}>
              <strong>{row.minDate}</strong>
              <strong>{row.maxDate}</strong>
              <span style={{ position: 'absolute', top: '9px', left: '48px' }}>-</span>
            </Grid>
          </>
        );
      }
    },
    {
      key: 'amount',
      name: 'AMOUNT',
      width: 135,
      editable: enableEditing,
      renderEditCell: textEditor,
      cellClass() {
        return "rdg-text-right";
      },
      renderCell(props) {
        const value = props.row.amount;
        return (
          <Grid item >
            {formatNumberToString(value)}
          </Grid>
        );
      },
      renderHeaderCell: (p) => (
        <FilterRenderer {...p}>
          {({ filters, ...rest }) => {
            return (
              <Grid container sx={{...removeNumberArrowsStyle}}>
                <Grid item sx={margin1.bottom}>
                  <input
                    type={"number"}
                    placeholder="From"
                    style={{...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.amountMin}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        amountMin: e.target.value
                      })
                    }
                  />
                </Grid>
                <Grid item>
                  <input
                    type={"number"}
                    placeholder="To"
                    style={{...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.amountMax}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        amountMax: e.target.value
                      })
                    }
                  />
                </Grid>
              </Grid>
            )
          }}
        </FilterRenderer>
      ),
      renderSummaryCell({ row }) {
        return (
          <>
            <Grid style={{fontSize: "14px"}} container justifyContent="center">
              <strong>{formatNumberToString(row.amount)}</strong>
            </Grid>
            <Grid style={dataGridSummaryTitle} container justifyContent="center">
              <strong>TOTAL(AMOUNT)</strong>
            </Grid>

          </>
        );
      }
    },
    {
      key: 'balanceAmount',
      name: 'BALANCE',
      width: 135,
      editable: enableEditing,
      renderEditCell: textEditor,
      cellClass() {
        return "rdg-text-right";
      },
      renderCell(props) {
        const value = props.row.balanceAmount;
        return (
          <Grid item >
            { value ? formatNumberToString(value): '' }
          </Grid>
        );
      },
      renderHeaderCell: (p) => (
        <FilterRenderer {...p}>
          {({ filters, ...rest }) => {
            return (
              <Grid container sx={{ ...removeNumberArrowsStyle }}>
                <Grid item sx={margin1.bottom}>
                  <input
                    type={"number"}
                    placeholder="From"
                    style={{ ...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.balanceAmountMin}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        balanceAmountMin: e.target.value
                      })
                    }
                  />
                </Grid>
                <Grid item>
                  <input
                    type={"number"}
                    placeholder="To"
                    style={{...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.balanceAmountMax}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        balanceAmountMax: e.target.value
                      })
                    }
                  />
                </Grid>
              </Grid>
            )
          }}
        </FilterRenderer>
      ),
      renderSummaryCell({ row }) {
        // for the case where the balance movement is 0, we still want to show it
        return typeof row.balanceAmountMovement === "number" &&
          <>
            <Grid style={{fontSize: "14px"}} container justifyContent="center"  sx={[dataGridSummaryTitle, formatNumberToString(row.balanceAmountMovement) !== formatNumberToString(row.amount) ? { color: "#9b1f11ff" } : { color: "green" }]}>
              <strong>{ concat( row.balanceAmountMovement <= 0 ? '' : '+',  formatNumberToString(row.balanceAmountMovement))}</strong>
            </Grid>
            <Grid container justifyContent="center" sx={dataGridSummaryTitle}>
              <strong>BALANCE MOVEMENT</strong>
               </Grid>
          </>
      }
    },
    {
      key: 'calculatedBalance',
      name: '',
      width: 135,
      headerCellClass: "rdg-cell-calculated-balance",
      summaryCellClass: "rdg-cell-calculated-balance",
      cellClass() {
        return "rdg-text-right rdg-cell-calculated-balance";
      },
      renderCell(props) {
        const value = props.row.calculatedBalance;
        return (
          <Grid item>
            { value ? formatNumberToString(value): '' }
          </Grid>
        );
      },
      renderHeaderCell: (p) => (
        <Grid container sx={{paddingBottom: "40px"}}>
          <Grid item>
            CALCULATED
          </Grid>
          <Grid item>
            BALANCE
          </Grid>
        </Grid>
      ),
      renderSummaryCell({ row }) {
        return (typeof row.calBalanceAmountMovement === "number" &&
          <>
            <Grid style={{fontSize: "14px"}} container justifyContent="center"  sx={[dataGridSummaryTitle, formatNumberToString(row.calBalanceAmountMovement) !== formatNumberToString(row.amount) ? { color: "#9b1f11ff" } : { color: "green" }]}>
              <strong>{ concat( row.calBalanceAmountMovement <= 0 ? '' : '+',  formatNumberToString(row.calBalanceAmountMovement))}</strong>
            </Grid>
            <Grid container justifyContent="center" sx={dataGridSummaryTitle}>
              <strong>BALANCE MOVEMENT</strong>
               </Grid>
          </>
        );
      }
    },
    {
      key: 'txnCode',
      name: 'TXN CODE',
      width: 86,
      editable: enableEditing,
      renderEditCell: textEditor,
      renderHeaderCell: (p) => (
        <FilterRenderer {...p}>
          {({ filters, ...rest }) => {
            return (
              <Grid container sx={{paddingBottom: "34px"}}>
                <Grid item>
                  <input
                    style={{...dataGridHeaderCellFilter.base, ...dataGridHeaderCellFilter.full}}
                    {...rest}
                    value={filters.txnCode}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        txnCode: e.target.value
                      })
                    }
                    }
                  />
                </Grid>
              </Grid>
            )
          }}
        </FilterRenderer>
      )
    },
  ];

  /**
   *
   * @param row
   * @returns {*}
   */
  const rowKeyGetter = (row) => {
    return row.transactionId;
  }

  /**
   *
   * @param fRows
   * @param row
   */
  const handleRowChange = (fRows, row) => {
    setUpdatingSaving("Updating")
    let updatedRowIndex = row.indexes[0]
    let fRowsCopy = deepCopy(fRows)
    let rowsCopy = deepCopy(rows)

    let newArray = []
    const indexFrom = rowsCopy.findIndex(item => item.transactionId === fRowsCopy[updatedRowIndex].transactionId)
    rowsCopy[indexFrom] = fRowsCopy[updatedRowIndex]

    // If columns is either 'key' or 'bookingDateTime'
    if (row.column.key === 'key') {
      let indexTo = null

      // If columns is 'key'
      if (row.column.key === 'key') {
        // set destination indexTo with new value of the input
        indexTo = (parseInt(fRowsCopy[updatedRowIndex].key) - 1)

        // If value isn't a valid number => notify and return
        if (typeof indexTo !== 'number' || isNaN(indexTo)) {
          notify('Invalid key, must be a integer', { type: 'warning', autoHideDuration: 2000 });
          setUpdatingSaving(null)
          return
        // Else If value is a valid number
        } else {
          // If the new index is below 0, set key to 0
          if (indexTo < 0) {
            indexTo = 0
            rowsCopy[indexFrom].key = 1
          // Else if the new index is above the amount of transactions, set key to length of rowsCopy.
          } else if (indexTo >= rowsCopy.length) {
            indexTo = (rowsCopy.length - 1)
            rowsCopy[indexFrom].key = rowsCopy.length
          }
        }
      }

      // If columns is 'bookingDateTime'
      if (row.column.key === 'bookingDateTime') {
        const newDate = moment(rowsCopy[indexFrom].bookingDateTime)
        // If value isn't a valid date => notify and return
        if (!newDate._isValid) {
          notify('Invalid date', { type: 'warning', autoHideDuration: 2000 });
          setUpdatingSaving(null)
          return
        }

        let isMovedAboveCurrentIndex = true
        // set destination indexTo by comparing dates from existing values in the set of rowsCopy
        /* eslint-disable-next-line array-callback-return */
        indexTo = rowsCopy.findIndex((item, i) => {
          const intermediateDate = moment(item.bookingDateTime)
          if (i > indexFrom)
            isMovedAboveCurrentIndex = false
          if (i !== indexFrom)
            return newDate.isSameOrAfter(intermediateDate)
        })
        if (indexTo === -1) indexTo = rowsCopy.length - 1
        else if (!isMovedAboveCurrentIndex) indexTo -= 1
      }

      // Reset all key values
      if (indexFrom < indexTo) {
        for (let i = 0; i < rowsCopy.length; i++) {
          if (i < (indexFrom) || i > indexTo) {
            newArray.push(rowsCopy[i])
          } else if (i >= indexFrom && i < indexTo) {
            rowsCopy[i + 1].key = rowsCopy[i + 1].key - 1
            newArray.push(rowsCopy[i + 1])
          } else if (i === indexTo) {
            if (row.column.key === 'key')
              rowsCopy[indexFrom].bookingDateTime = rowsCopy[i].bookingDateTime
            if (row.column.key === 'bookingDateTime')
              rowsCopy[indexFrom].key = i + 1
            rowsCopy[indexFrom].key = parseInt(rowsCopy[indexFrom].key)
            newArray.push(rowsCopy[indexFrom])
          }
        }
      } else if (indexFrom > indexTo) {
        for (let i = 0; i < rowsCopy.length; i++) {
          if (i < indexTo || i > (indexFrom)) {
            newArray.push(rowsCopy[i])
          } else if (i <= indexFrom && i > indexTo) {
            rowsCopy[i - 1].key = rowsCopy[i - 1].key + 1
            newArray.push(rowsCopy[i - 1])
          } else if (i === indexTo) {
            if (row.column.key === 'key')
              rowsCopy[indexFrom].bookingDateTime = rowsCopy[i].bookingDateTime
            if (row.column.key === 'bookingDateTime')
              rowsCopy[indexFrom].key = i + 1
            rowsCopy[indexFrom].key = parseInt(rowsCopy[indexFrom].key)
            newArray.push(rowsCopy[indexFrom])
          }
        }
      } else {
        newArray = deepCopy(rowsCopy)
      }
    } else {
      // If columns is either 'amount' or 'balanceAmount'
      if (row.column.key === 'amount' || row.column.key === 'balanceAmount') {
        let value = Number(fRowsCopy[updatedRowIndex][row.column.key]).toString()

        // If value isn't a valid number => notify and return
        if (isNaN(value) || value === '') {
          notify('Invalid number, please try again', { type: 'warning', autoHideDuration: 3000 });
          setUpdatingSaving(null)
          return
        } else if (
          ((value.indexOf('-') === 0) && (value.indexOf('.') === 1)) ||
          (value.indexOf('.') === 0)
        ) {
          value = +value
        }

        const parsedValue = JSON.parse(JSON.parse(value).toFixed(2))
        // If value isn't a valid number => notify and return
        if (typeof parsedValue !== 'number') {
          notify('Invalid number, please try again', { type: 'warning', autoHideDuration: 3000 });
          setUpdatingSaving(null)
          return
        }

        if (
          (row.column.key === 'amount') &&
          (rowsCopy[indexFrom].insertedRow)
        ) {
          if (!sortASCDirection) {
            let newCalculatedBalance = null
            if (indexFrom !== 0) {
              if (
                ((rowsCopy[indexFrom - 1].balanceAmount !== null) && (rowsCopy[indexFrom - 1].balanceAmount !== undefined)) &&
                ((rowsCopy[indexFrom - 1].amount !== null) && (rowsCopy[indexFrom - 1].amount !== undefined))
              ) {
                newCalculatedBalance = parseFloat(rowsCopy[indexFrom - 1].balanceAmount) - parseFloat(rowsCopy[indexFrom - 1].amount)
              } else if ((rowsCopy[indexFrom + 1].balanceAmount !== null) && (rowsCopy[indexFrom + 1].balanceAmount !== undefined)) {
                newCalculatedBalance = parseFloat(rowsCopy[indexFrom + 1].balanceAmount) + parseFloat(value)
              } else {
                newCalculatedBalance = !rowsCopy[indexFrom].balanceAmount
                  ? newCalculatedBalance
                  : rowsCopy[indexFrom].balanceAmount
              }
            }

            rowsCopy[indexFrom].balanceAmount = newCalculatedBalance
            rowsCopy[indexFrom].calculatedBalance = newCalculatedBalance
            rowsCopy[indexFrom].insertedRow = (!rowsCopy[indexFrom].amount || !rowsCopy[indexFrom].balanceAmount)
          } else {
            let newCalculatedBalance = null
            if (indexFrom !== rowsCopy.length - 1) {
              if ((rowsCopy[indexFrom + 1].balanceAmount !== null) && (rowsCopy[indexFrom + 1].balanceAmount !== undefined)) {
                newCalculatedBalance = parseFloat(rowsCopy[indexFrom + 1].balanceAmount) + parseFloat(value)
              } else if (
                ((rowsCopy[indexFrom - 1].balanceAmount !== null) && (rowsCopy[indexFrom - 1].balanceAmount !== undefined)) &&
                ((rowsCopy[indexFrom - 1].amount !== null) && (rowsCopy[indexFrom - 1].amount !== undefined))
              ) {
                newCalculatedBalance = parseFloat(rowsCopy[indexFrom - 1].balanceAmount) - parseFloat(rowsCopy[indexFrom - 1].amount)
              } else {
                newCalculatedBalance = !rowsCopy[indexFrom].balanceAmount
                  ? newCalculatedBalance
                  : rowsCopy[indexFrom].balanceAmount
              }
            }

            rowsCopy[indexFrom].balanceAmount = newCalculatedBalance
            rowsCopy[indexFrom].calculatedBalance = newCalculatedBalance
            rowsCopy[indexFrom].insertedRow = (!rowsCopy[indexFrom].amount || !rowsCopy[indexFrom].balanceAmount)
          }
        }
      }

      newArray = deepCopy(rowsCopy)
    }

    setRows((row.column.key !== 'description' && row.column.key !== 'txnCode')
      ? initKeyAndCalculateValidateAmounts(newArray, sortASCDirection)
      : newArray
    )
    setUpdatingSaving(null)
    checkTokenExpiryAndRefresh();
  }

  /**
   *
   * @param rowIdx
   */
  const scrollToRow = () => {
    const rowsCopy = sortASCDirection ? reverseArray(rows) : rows
    const rowIdx = rowsCopy.findIndex((item, index) =>
      item.balanceAmountVsCalculatedBalanceError && (index > scrollErrorRowIdx) || item.insertedRow && (index > scrollErrorRowIdx)
    )
    setScrollErrorRowIdx(rowIdx)
    gridRef.current?.scrollToCell({ rowIdx: rowIdx + 2 }) //Plus 2 as the row indexes start with the header and subHeader
  }

  return (
    <FilterContext.Provider value={filters}>
      {loading &&
        <Grid container alignItems={"center"} justifyContent={"center"} height={300}>
          <CircularProgress
            size={26}
            thickness={5}
            color="primary"
          />
          <span style={margin1.left}>Loading Transactions</span>
        </Grid>
        ||
        <Grid>
          <TransactionTopBulkActionButton
            {...props}
            sx={padding2.y}
            rows={rows}
            setRows={setRows}
            deletedRows={deletedRows}
            setDeletedRows={setDeletedRows}
            insertedRows={insertedRows}
            setInsertedRows={setInsertedRows}
            selectedTxnIds={selectedTxnIds}
            setSelectedTxnIds={setSelectedTxnIds}
            scrollToRow={scrollToRow}
            scrollErrorRowIdx={scrollErrorRowIdx}
            selectedTxnRows={selectedTxnRows}
            sortASCDirection={sortASCDirection}
            setUpdatingSaving={setUpdatingSaving}
            errors={errors}
          />
          <Grid className={"data-grid-container " + (updatingSaving ? "saving" : '')}>
            { updatingSaving &&
              <Grid container alignItems={"center"} justifyContent={"center"} sx={{...dataGrid.savingSpinner, height: "calc(100vh - 484px + 100px)"}}>
                <Grid item sx={dataGrid.savingSpinner.inner} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                  <CircularProgress
                    size={26}
                    thickness={5}
                    color="primary"
                  />
                  <span style={margin1.left}>{updatingSaving}</span>
                </Grid>
              </Grid>
            }

            <DataGrid
              className='rdg-light rdg-armalytix'
              ref={gridRef}
              columns={columns}
              rows={filteredRows}
              rowHeight={38}
              rowKeyGetter={rowKeyGetter}
              onRowsChange={handleRowChange}
              selectedRows={selectedTxnIds}
              onSelectedRowsChange={setSelectedTxnIds}
              headerRowHeight={100}
              rowClass={(row, index) => {
                if (index === scrollErrorRowIdx && (row.balanceAmountVsCalculatedBalanceError || row.insertedRow))
                  return 'rdg-row-highlight0'

                if (row.insertedRow)
                  return 'rdg-row-highlight2'

                if (row.balanceAmountVsCalculatedBalanceError)
                  return 'rdg-row-highlight1'
              }}
              topSummaryRows={summaryRows}
              summaryRowHeight={60}
            />
          </Grid>
          {enableEditing &&
            <TransactionBottomBulkActionButton
              {...props}
              sx={padding2.y}
              rows={rows}
              setRows={setRows}
              deletedRows={deletedRows}
              setDeletedRows={setDeletedRows}
              insertedRows={insertedRows}
              setInsertedRows={setInsertedRows}
              originalData={originalData}
              setOriginalData={setOriginalData}
              originalFullData={originalFullData}
              setOriginalFullData={setOriginalFullData}
              setSelectedTxnIds={setSelectedTxnIds}
              updatingSaving={updatingSaving}
              setUpdatingSaving={setUpdatingSaving}
              sortASCDirection={sortASCDirection}
              errors={errors}
              pageNumber={pageNumber}
              rowNumber={rowNumber}
            />
          }
        </Grid>
      }
    </FilterContext.Provider>
  );
}

/**
 * Transactions displayed at the top of the data grid
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionTopBulkActionButton = (props) => {
  const { selectedTxnIds } = props;

  return (
    <Grid container item justifyContent={'space-between'} sx={{ ...padding1.y, height: '48px' }}>
      <Grid item>
        {
          (selectedTxnIds && selectedTxnIds.size > 0) &&
          <>
            <TransactionsDeleteActionButton {...props} />
            <TransactionsInsertActionButton {...props} />
          </>
        }
      </Grid>
      <Grid item>
        <TransactionsScrollToActionButton {...props} />
      </Grid>
    </Grid>
  );
};

/**
 * Transactions displayed at the top of the data grid
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionBottomBulkActionButton = (props) => {
  return (
    <Grid container item justifyContent={'space-between'} sx={padding1.y}>
      <Grid><small>{props.pageNumber !== null ? 'Page' : null} {props.pageNumber} {props.rowNumber !== null ? ' - Row' : null} {props.rowNumber}</small></Grid>
      <TransactionsSaveActionButton {...props} />
    </Grid>
  );
};

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionsDeleteActionButton = (props) => {
  const {
    rows,
    setRows,
    deletedRows,
    setDeletedRows,
    insertedRows,
    setInsertedRows,
    selectedTxnIds,
    setSelectedTxnIds,
    sortASCDirection,
  } = props;
  const [openDelete, setOpenDelete] = useState(false);

  const handleDelete = () => {
    const selectedIds = [...selectedTxnIds.values()]
    const tmpDeletedRows = [...new Set([...deletedRows ,...selectedIds])]
    const insertedDeletedIds = insertedRows.filter(id => tmpDeletedRows.includes(id))
    const newDeletedRows = tmpDeletedRows.filter(id => !insertedDeletedIds.includes(id))
    const newInsertedRows = insertedRows.filter(item => !insertedDeletedIds.includes(item))

    setDeletedRows(newDeletedRows)
    setInsertedRows(newInsertedRows)

    const rowsCopy = deepCopy(rows)
    // Remove deleted rows from list of rows
    let updatedRows = rowsCopy.filter((item) => !selectedIds.includes(item.transactionId))
    updatedRows.forEach((item, index) => item.key = index + 1)

    setRows(updatedRows.length ? initKeyAndCalculateValidateAmounts(updatedRows, sortASCDirection) : [])

    setSelectedTxnIds(new Set())
    setOpenDelete(false);
    checkTokenExpiryAndRefresh();
  }

  return (
    <>
      <RaButton
        id="matters-transactions-delete-open"
        sx={{ ...topActionButtonBaseStyle() }}
        size="small"
        variant="outlined"
        startIcon={<DeleteIcon />}
        label={`${ActionTitles.DELETE} - ${selectedTxnIds.size} TXN${selectedTxnIds.size > 1 ? 'S' : ''} SELECTED`}
        onClick={() => { setOpenDelete(true) }}
      >
        <DeleteIcon />
      </RaButton>
      <CustomDialog
        openDialog={openDelete}
        closeDialog={() => { setOpenDelete(false) }}
        cancelBtnTitle="CLOSE"
        submitBtnTitle="DELETE"
        submitBtnIcon={<DeleteOutlinedIcon />}
        handleSubmit={handleDelete}
        content={
          <>
            <p>
              Are you sure you want to delete
              {
                (selectedTxnIds.size > 1)
                  ? ` these ${selectedTxnIds.size} transactions`
                  : ` this transaction`
              }
              ?
            </p>
          </>
        }
      />
    </>
  )
}

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionsSaveActionButton = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const {
    matterId,
    accountId,
    rows,
    setRows,
    deletedRows,
    setDeletedRows,
    setInsertedRows,
    originalData,
    setOriginalData,
    originalFullData,
    setOriginalFullData,
    setSelectedTxnIds,
    updatingSaving,
    setUpdatingSaving,
    errors,
    sortASCDirection,
  } = props;
  const [openSave, setOpenSave] = useState(false);

  const hasDataErrors = errors.filter(item => item.balanceAmountVsCalculatedBalanceError)
  const hasInsertErrors = errors.filter(item => item.insertedRow)

  const handleCancel = () => {
    const originalRows = deepCopy(originalFullData)
    setRows(initKeyAndCalculateValidateAmounts(originalRows, sortASCDirection))
    setDeletedRows([])
    setInsertedRows([])
    setSelectedTxnIds(new Set())
  }

  const handleSave = async () => {
    setOpenSave(false)
    setUpdatingSaving("Saving")

    await dataProvider
      .deleteSaveFetchTransactions({
        saveData: { transactions: rows },
        deleteData: { transactionIds: deletedRows },
        meta: { matterId, accountId }
      })
      .then(async(content) => {
        let data = [];
        if (content && content.length)
          data = initKeyAndCalculateValidateAmounts(content, sortASCDirection, true)
        setRows(data);
        setOriginalData(mapReductionTransactions(deepCopy(data)))
        setOriginalFullData(deepCopy(data))
        setDeletedRows([])
        setInsertedRows([])
        notify('Transactions saved successfully', { autoHideDuration: 2000 });
        refresh();
      }).catch((e) => {
        notify(`Something went wrong while ${updatingSaving.toLowerCase()} transactions.`, { type: 'error', autoHideDuration: 2000 });
      }).finally(() => {
        setUpdatingSaving(null)
      });
  }

  return (
    <Grid item>
      <RaButton
        id="matters-transactions-cancel"
        sx={{ ...topActionButtonBaseStyle("gray", "#fff", "#eaeaea", "#b2afaf", "#9B9999") }}
        size="small"
        variant="outlined"
        startIcon={<CancelIcon />}
        label={ActionTitles.CANCEL}
        disabled={isDataUnchanged(rows, originalData)}
        onClick={handleCancel}
      >
        <CancelIcon />
      </RaButton>
      <RaButton
        id="matters-transactions-save-open"
        sx={{ ...topActionButtonBaseStyle() }}
        size="small"
        startIcon={<CheckCircleIcon />}
        label={ActionTitles.SAVE}
        disabled={isDataUnchanged(rows, originalData) || updatingSaving}
        onClick={() => { setOpenSave(true) }}
      >
        <CheckCircleIcon />
      </RaButton>
      <CustomDialog
        openDialog={openSave}
        closeDialog={() => { setOpenSave(false) }}
        cancelBtnTitle={ActionTitles.CANCEL}
        submitBtnTitle={ActionTitles.SAVE}
        submitBtnIcon={<CheckCircleIcon />}
        hideSubmitBtn={hasInsertErrors.length > 0}
        handleSubmit={handleSave}
        content={
          <>
            {
              (hasInsertErrors && hasInsertErrors.length > 0) &&
              <>
                <p>Some inserted transactions are missing an amount and/or balance.</p>
                <p>Please complete them before saving.</p>
              </>
            }
            {
              (!hasInsertErrors || hasInsertErrors.length === 0) &&
              <>
                <p>
                  {hasDataErrors && hasDataErrors.length > 0 && <>Some transactions still seem to have data integrity
                    issues.<br/>Do you want to proceed and save?</>}
                </p>
                <p style={padding2.bottom}>This action cannot be undone.</p>
              </>
            }
          </>
        }
      />
    </Grid>
  )
}

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionsInsertActionButton = (props) => {
  const {
    rows,
    setRows,
    selectedTxnRows,
    insertedRows,
    setInsertedRows,
    setSelectedTxnIds,
    sortASCDirection,
    setUpdatingSaving,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleInsert = (linesCount) => {
    const rowSelected = deepCopy(selectedTxnRows[sortASCDirection ? 0 : selectedTxnRows.length - 1])
    let rowsCopy = deepCopy(rows)
    const newRows = []
    for (let i = 0; i < linesCount; i++) {
      newRows.push({
        accountId: rowSelected.accountId,
        amount: null,
        balanceAmount: null,
        balanceCurrency: rowSelected.balanceCurrency,
        bankId: rowSelected.bankId,
        bookingDateTime: rowSelected.bookingDateTime,
        currency: rowSelected.currency,
        description: null,
        key: sortASCDirection ? rowSelected.key + i : rowSelected.key + 1 + i,
        txnCode: null,
        transactionId: uuidv4(),
        insertedRow: true,
      })
    }

    rowsCopy.splice((newRows[0].key - 1), 0, ...newRows)
    for (let i = newRows[linesCount - 1].key; i < rowsCopy.length; i++) {
      rowsCopy[i].key = rowsCopy[i].key + 1
    }

    setInsertedRows([...new Set([...insertedRows, ...newRows.map(newRow => newRow.transactionId)])])

    setSelectedTxnIds(new Set())
    setRows(initKeyAndCalculateValidateAmounts(rowsCopy, sortASCDirection))
    setUpdatingSaving(null)
    checkTokenExpiryAndRefresh();
  }

  const handleInsertClickOption = (linesCount) => {
    handleCloseDropdown();
    handleInsert(linesCount)
  }

  const handleClickDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const insertCount = [1,2,3,4,5,6,7,8,9,10]

  return (
    <>
      <Button
        id="insert-button"
        aria-controls={open ? "insert-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClickDropdown}
        startIcon={<AddCircleOutlineIcon />}
        size="small"
        sx={topActionButtonBaseStyle()}
      >
        {ActionTitles.INSERT}
        <ArrowDropDown />
      </Button>
      <Menu
        id="insert-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseDropdown}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        MenuListProps={{
          "aria-labelledby": "insert-button"
        }}
      >
        {
          insertCount.map(item => <MenuItem id={`matters-transactions-insert-option-${item}`} aria-label={item} onClick={() => handleInsertClickOption(item)}>{item}</MenuItem>)
        }
      </Menu>
    </>
  )
}

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
const TransactionsScrollToActionButton = (props) => {
  const {
    rows,
    scrollToRow,
    scrollErrorRowIdx,
    sortASCDirection,
    errors,
  } = props;
  const errorIndex = errors.findIndex((item) => {
    return sortASCDirection
      ? ((rows.length - item.key) === scrollErrorRowIdx)
      : ((item.key - 1) === props.scrollErrorRowIdx)
  })

  const hasDataErrors = errors.filter(item => item.balanceAmountVsCalculatedBalanceError)
  const hasInsertErrors = errors.filter(item => item.insertedRow)

  return (
    <>
      {
        (errors.length > 0) &&
        <>
          <Grid container sx={dataGridErrorActions} alignItems={"center"}>
            { hasDataErrors.length > 0 &&
              <Grid container item class="error-legend amount-vs-balance-amount-error">
                <Grid container item>
                  <Tooltip title="The balance amount does not match the calculated balance" placement="bottom">
                    <Grid item className="legend-color legend-color-highlight1" sx={{...margin1.right, ...cursor.pointer}}></Grid>
                  </Tooltip>
                  {/*<Tooltip title={"The previous row's \"transaction's amount - balance amount\" does not match the row's balance amount"} placement="bottom">*/}
                  {/*  <Grid item className="legend-color legend-color-highlight2"></Grid>*/}
                  {/*</Tooltip>*/}
                </Grid>
              </Grid>
            }
            { hasInsertErrors.length > 0 &&
              <Grid container item class="error-legend insert-error">
                <Grid container item>
                  <Tooltip title="The inserted transaction has not been filled fully" placement="bottom">
                    <Grid item className="legend-color legend-color-highlight2" sx={{...margin1.right, ...cursor.pointer}}></Grid>
                  </Tooltip>
                  {/*<Tooltip title={"The previous row's \"transaction's amount - balance amount\" does not match the row's balance amount"} placement="bottom">*/}
                  {/*  <Grid item className="legend-color legend-color-highlight2"></Grid>*/}
                  {/*</Tooltip>*/}
                </Grid>
              </Grid>
            }
            <Grid item>
              <RaButton
                id="matters-transactions-find-error"
                sx={{ ...topActionButtonBaseStyle("gray", "#fff", "#eaeaea", "#b2afaf", "#9B9999") }}
                size="small"
                variant="outlined"
                endIcon={<ArrowDownwardIcon />}
                startIcon={<WarningAmberIcon />}
                label={`${ActionTitles.FIND} ${errors.length > 1 ? ActionTitles.ERRORS : ActionTitles.ERROR} ${(errorIndex === -1) ? '' : ' - ' + (errorIndex + 1) + '/' + errors.length}`}
                onClick={() => {
                  scrollToRow()
                }}
              >
                <WarningAmberIcon />
                <ArrowDownwardIcon />
              </RaButton>
            </Grid>
          </Grid>
        </>
      }
    </>
  )
}
